import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { BACKEND_URL } from '../utils/config';
import { REFERRALS_ENABLED } from '../utils/featureFlags';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import MilestoneProgressBar from '../components/team/MilestoneProgressBar';
import GoalCelebrationBanner from '../components/team/GoalCelebrationBanner';
import SetGoalModal from '../components/team/SetGoalModal';
import {
  Heart,
  Users,
  Share2,
  ChevronRight,
  Loader2,
  Target,
  BadgeCheck,
  ArrowLeft,
  ExternalLink,
  Copy,
  Check,
  Settings,
  Link2,
  Twitter,
  Facebook,
  Linkedin,
} from 'lucide-react';

const TeamPageDetailPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user, token } = useAuth();
  
  const [teamPage, setTeamPage] = useState(null);
  const [metrics, setMetrics] = useState(null);
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  const [showGoalModal, setShowGoalModal] = useState(false);
  
  // Referral code generation
  const [showReferralModal, setShowReferralModal] = useState(false);
  const [generatedReferralCode, setGeneratedReferralCode] = useState(null);
  const [referralLoading, setReferralLoading] = useState(false);

  useEffect(() => {
    const fetchTeamPage = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/team-pages/slug/${slug}`);
        if (!response.ok) {
          throw new Error('Team page not found');
        }
        const data = await response.json();
        setTeamPage(data);
        
        // Fetch metrics
        const metricsRes = await fetch(`${BACKEND_URL}/api/team-pages/${data.id}/metrics`);
        if (metricsRes.ok) {
          setMetrics(await metricsRes.json());
        }
        
        // Fetch progress
        const progressRes = await fetch(`${BACKEND_URL}/api/team-pages/${data.id}/progress`);
        if (progressRes.ok) {
          setProgress(await progressRes.json());
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    
    fetchTeamPage();
  }, [slug]);

  const handleCopyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: teamPage?.title,
          text: `Support ${teamPage?.owner_name}'s fundraising for ${teamPage?.parent_campaign?.title}`,
          url: window.location.href,
        });
      } catch (err) {
        handleCopyLink();
      }
    } else {
      handleCopyLink();
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
    }).format(amount || 0);
  };

  // Generate team page-specific referral code
  const handleGenerateReferralCode = async () => {
    if (!token) {
      alert('Please login to generate a referral code');
      return;
    }
    
    setReferralLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/referrals/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          team_page_id: teamPage.id,
          referral_type: 'team'
        })
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to generate code');
      }
      
      const data = await response.json();
      setGeneratedReferralCode(data.referral_code);
      setShowReferralModal(true);
    } catch (error) {
      console.error('Failed to generate referral code:', error);
      alert(error.message || 'Failed to generate referral code');
    } finally {
      setReferralLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <Header />
        <main className="flex-1 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
        </main>
        <Footer />
      </div>
    );
  }

  if (error || !teamPage) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <Header />
        <main className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-foreground mb-2">Team Page Not Found</h2>
            <p className="text-muted-foreground mb-4">{error || 'This fundraising page does not exist.'}</p>
            <Link to="/discover">
              <Button className="bg-emerald-500 hover:bg-emerald-600">
                Discover Campaigns
              </Button>
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const progressPercent = teamPage.goal_amount 
    ? Math.min((teamPage.amount_raised / teamPage.goal_amount) * 100, 100)
    : 0;

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header />
      
      <main className="flex-1">
        {/* Breadcrumb */}
        <div className="bg-card border-b border-border">
          <div className="max-w-4xl mx-auto px-4 py-3">
            <div className="flex items-center text-sm text-muted-foreground">
              <Link to="/" className="hover:text-emerald-400">Home</Link>
              <ChevronRight className="w-4 h-4 mx-2" />
              <Link to={`/campaign/${teamPage.parent_campaign_id}`} className="hover:text-emerald-400">
                {teamPage.parent_campaign?.title || 'Campaign'}
              </Link>
              <ChevronRight className="w-4 h-4 mx-2" />
              <span className="text-foreground">Team Fundraiser</span>
            </div>
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-4 py-8">
          {/* Back to Campaign Link */}
          <Link to={`/campaign/${teamPage.parent_campaign_id}`} className="inline-flex items-center gap-2 text-emerald-500 hover:text-emerald-400 mb-6">
            <ArrowLeft className="w-4 h-4" />
            Back to main campaign
          </Link>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-6">
              {/* Cover Image */}
              <div className="rounded-2xl overflow-hidden bg-secondary">
                <img
                  src={teamPage.cover_image_url || teamPage.parent_campaign?.image || 'https://images.unsplash.com/photo-1559027615-cd4628902d4a?w=800'}
                  alt={teamPage.title}
                  className="w-full h-64 object-cover"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(teamPage.title || 'Team Page')}&background=10b981&color=fff&size=400`;
                  }}
                />
              </div>

              {/* Title & Fundraiser Info */}
              <div>
                <Badge className="bg-violet-500/20 text-violet-400 border-violet-500/30 mb-3">
                  <Users className="w-3 h-3 mr-1" /> Team Fundraiser
                </Badge>
                <h1 className="text-3xl font-bold text-foreground mb-4">{teamPage.title}</h1>
                
                <div className="flex items-center gap-4">
                  <img
                    src={teamPage.owner_avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(teamPage.owner_name)}`}
                    alt={teamPage.owner_name}
                    className="w-12 h-12 rounded-full border-2 border-emerald-500"
                  />
                  <div>
                    <p className="font-medium text-foreground flex items-center gap-2">
                      {teamPage.owner_name}
                      <BadgeCheck className="w-4 h-4 text-emerald-500" />
                    </p>
                    <p className="text-sm text-muted-foreground">
                      Fundraising for "{teamPage.parent_campaign?.title}"
                    </p>
                  </div>
                </div>
              </div>

              {/* Story */}
              {teamPage.story && (
                <div className="bg-card rounded-xl p-6 border border-border">
                  <h3 className="font-semibold text-foreground mb-3">Why I'm Fundraising</h3>
                  <p className="text-muted-foreground whitespace-pre-wrap">{teamPage.story}</p>
                </div>
              )}

              {/* Parent Campaign Card */}
              <div className="bg-gradient-to-r from-emerald-500/10 to-teal-500/10 border border-emerald-500/30 rounded-xl p-5">
                <div className="flex items-center gap-2 mb-3">
                  <Target className="w-5 h-5 text-emerald-500" />
                  <span className="font-medium text-foreground">Supporting Campaign</span>
                </div>
                <Link to={`/campaign/${teamPage.parent_campaign_id}`} className="block group">
                  <div className="flex items-center gap-4">
                    <img
                      src={teamPage.parent_campaign?.image || `https://ui-avatars.com/api/?name=${encodeURIComponent(teamPage.parent_campaign?.title || 'Campaign')}&background=10b981&color=fff&size=160`}
                      alt={teamPage.parent_campaign?.title}
                      className="w-20 h-20 rounded-lg object-cover bg-secondary"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(teamPage.parent_campaign?.title || 'Campaign')}&background=10b981&color=fff&size=160`;
                      }}
                    />
                    <div className="flex-1">
                      <h4 className="font-semibold text-foreground group-hover:text-emerald-400 transition-colors">
                        {teamPage.parent_campaign?.title}
                      </h4>
                      <p className="text-sm text-muted-foreground">
                        {formatCurrency(teamPage.parent_campaign?.raised)} raised of {formatCurrency(teamPage.parent_campaign?.goal)}
                      </p>
                    </div>
                    <ExternalLink className="w-5 h-5 text-muted-foreground group-hover:text-emerald-400" />
                  </div>
                </Link>
              </div>

              {/* Recent Donations */}
              {metrics?.recent_donations?.length > 0 && (
                <div className="bg-card rounded-xl p-6 border border-border">
                  <h3 className="font-semibold text-foreground mb-4">Recent Supporters</h3>
                  <div className="space-y-3">
                    {metrics.recent_donations.map((donation, i) => (
                      <div key={i} className="flex items-center gap-3 pb-3 border-b border-border last:border-0 last:pb-0">
                        <div className="w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center">
                          <Heart className="w-5 h-5 text-emerald-400" />
                        </div>
                        <div className="flex-1">
                          <p className="font-medium text-foreground">{donation.donor_name}</p>
                          <p className="text-xs text-muted-foreground">
                            {new Date(donation.created_at).toLocaleDateString()}
                          </p>
                        </div>
                        <span className="font-semibold text-emerald-400">{formatCurrency(donation.amount)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              <div className="sticky top-24 space-y-4">
                {/* Goal Celebration Banner */}
                {progress?.goal_completed && (
                  <GoalCelebrationBanner
                    goalAmount={teamPage.goal_amount}
                    amountRaised={teamPage.amount_raised}
                    donorCount={teamPage.donor_count}
                    teamPageTitle={teamPage.title}
                    onShare={handleShare}
                    variant="compact"
                  />
                )}

                {/* Donation Card */}
                <div className="bg-card rounded-2xl p-6 border border-border shadow-lg">
                  {/* Milestone Progress */}
                  <div className="mb-6">
                    <MilestoneProgressBar
                      raised={teamPage.amount_raised}
                      goalAmount={teamPage.goal_amount}
                      milestoneSteps={progress?.milestones_reached ? teamPage.milestone_steps : []}
                      milestonesReached={progress?.milestones_reached || []}
                      goalCompleted={progress?.goal_completed || false}
                      showLabels={true}
                      size="default"
                    />
                  </div>

                  {/* Stats */}
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div className="text-center p-3 bg-secondary rounded-lg">
                      <p className="text-2xl font-bold text-foreground">{teamPage.donor_count || 0}</p>
                      <p className="text-sm text-muted-foreground">Donors</p>
                    </div>
                    <div className="text-center p-3 bg-secondary rounded-lg">
                      <p className="text-2xl font-bold text-foreground">{metrics?.days_active || 0}</p>
                      <p className="text-sm text-muted-foreground">Days Active</p>
                    </div>
                  </div>

                  {/* Donate Button */}
                  <Link to={`/donate/${teamPage.parent_campaign_id}?team_page=${teamPage.id}`}>
                    <Button className="w-full bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white py-6 text-lg rounded-xl mb-3">
                      <Heart className="mr-2 w-5 h-5" /> Donate Now
                    </Button>
                  </Link>

                  {/* Share Button */}
                  <Button 
                    variant="outline" 
                    className="w-full"
                    onClick={handleShare}
                  >
                    {copied ? <Check className="w-4 h-4 mr-2" /> : <Share2 className="w-4 h-4 mr-2" />}
                    {copied ? 'Link Copied!' : 'Share This Page'}
                  </Button>
                </div>

                {/* Edit Button for Owner */}
                {user && user.id === teamPage.owner_user_id && (
                  <div className="space-y-2">
                    <Button 
                      variant="outline" 
                      className="w-full"
                      onClick={() => setShowGoalModal(true)}
                    >
                      <Settings className="w-4 h-4 mr-2" /> Edit Goal & Milestones
                    </Button>
                    {REFERRALS_ENABLED && (
                      <Button 
                        variant="outline" 
                        className="w-full border-violet-500/30 hover:bg-violet-500/10"
                        onClick={handleGenerateReferralCode}
                        disabled={referralLoading}
                        data-testid="generate-team-referral-btn"
                      >
                        {referralLoading ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Share2 className="w-4 h-4 mr-2 text-violet-400" />
                        )}
                        Generate Referral Link
                      </Button>
                    )}
                    <Link to={`/dashboard?mode=donor&tab=my-team-pages`}>
                      <Button variant="outline" className="w-full">
                        Manage Team Page
                      </Button>
                    </Link>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>
      
      {/* Set Goal Modal */}
      {user && user.id === teamPage?.owner_user_id && (
        <SetGoalModal
          open={showGoalModal}
          onOpenChange={setShowGoalModal}
          teamPageId={teamPage?.id}
          currentGoal={teamPage?.goal_amount}
          currentMilestones={teamPage?.milestone_steps || []}
          onSuccess={(data) => {
            setTeamPage(prev => ({
              ...prev,
              goal_amount: data.goal_amount,
              milestone_steps: data.milestone_steps,
            }));
            setProgress(prev => ({
              ...prev,
              milestones_reached: data.milestones_reached,
              goal_completed: data.goal_completed,
            }));
          }}
        />
      )}
      
      {/* Referral Code Modal — hidden when REFERRALS_ENABLED=false */}
      {REFERRALS_ENABLED && (
        <Dialog open={showReferralModal} onOpenChange={setShowReferralModal}>
        <DialogContent className="max-w-md bg-card border-border">
          <DialogHeader>
            <DialogTitle className="text-foreground text-xl flex items-center gap-2">
              <Share2 className="w-5 h-5 text-violet-400" />
              Your Referral Link
            </DialogTitle>
            <DialogDescription className="text-muted-foreground">
              Share this unique link to track referrals and earn rewards when people donate through your team page link.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Referral Code Display */}
            <div className="bg-violet-500/10 border border-violet-500/30 rounded-lg p-4">
              <label className="text-xs text-muted-foreground block mb-2">Your Referral Code</label>
              <div className="flex items-center gap-2">
                <code className="flex-1 text-lg font-mono font-bold text-violet-400" data-testid="team-referral-code-display">
                  {generatedReferralCode}
                </code>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={async () => {
                    try {
                      await navigator.clipboard.writeText(generatedReferralCode);
                      alert('Referral code copied!');
                    } catch (err) {
                      console.error('Failed to copy:', err);
                    }
                  }}
                  data-testid="copy-team-referral-code-btn"
                >
                  <Link2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
            
            {/* Full Referral Link */}
            <div className="bg-secondary rounded-lg p-4">
              <label className="text-xs text-muted-foreground block mb-2">Full Referral Link</label>
              <div className="flex items-center gap-2">
                <input 
                  type="text"
                  readOnly
                  value={`${window.location.origin}/fundraise/${teamPage?.slug}?ref=${generatedReferralCode}`}
                  className="flex-1 text-sm bg-background border border-border rounded px-2 py-1 text-foreground"
                  data-testid="team-referral-link-input"
                />
                <Button
                  size="sm"
                  variant="outline"
                  onClick={async () => {
                    try {
                      const url = `${window.location.origin}/fundraise/${teamPage?.slug}?ref=${generatedReferralCode}`;
                      await navigator.clipboard.writeText(url);
                      alert('Referral link copied!');
                    } catch (err) {
                      console.error('Failed to copy:', err);
                    }
                  }}
                  data-testid="copy-team-referral-link-btn"
                >
                  <Link2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
            
            {/* Share Buttons */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">Share on Social Media</label>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="flex-1"
                  onClick={() => {
                    const url = `${window.location.origin}/fundraise/${teamPage?.slug}?ref=${generatedReferralCode}`;
                    window.open(`https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(`Support my fundraiser: ${teamPage?.title}`)}`, '_blank');
                  }}
                >
                  <Twitter className="w-4 h-4 mr-1" /> Twitter
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="flex-1"
                  onClick={() => {
                    const url = `${window.location.origin}/fundraise/${teamPage?.slug}?ref=${generatedReferralCode}`;
                    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`, '_blank');
                  }}
                >
                  <Facebook className="w-4 h-4 mr-1" /> Facebook
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="flex-1"
                  onClick={() => {
                    const url = `${window.location.origin}/fundraise/${teamPage?.slug}?ref=${generatedReferralCode}`;
                    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`, '_blank');
                  }}
                >
                  <Linkedin className="w-4 h-4 mr-1" /> LinkedIn
                </Button>
              </div>
            </div>
            
            {/* Info Box */}
            <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-3">
              <p className="text-sm text-emerald-300">
                <strong>Tip:</strong> When someone donates using your referral link, you'll earn rewards. 
                Track your referral stats in the "Invite & Earn" section of your dashboard.
              </p>
            </div>
          </div>
          
          <div className="flex justify-end">
            <Button variant="outline" onClick={() => setShowReferralModal(false)}>
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      )}
      
      <Footer />
    </div>
  );
};

export default TeamPageDetailPage;
